import React from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Users, Building2, Calendar, Image, AlertCircle, CheckCircle } from "lucide-react";
import StatsCard from "@/components/dashboard/StatsCard";
import RecentActivity from "@/components/dashboard/RecentActivity";
import InspectorStatus from "@/components/dashboard/InspectorStatus";
import { Skeleton } from "@/components/ui/skeleton";
import { motion } from "framer-motion";

export default function Dashboard() {
  const { data: inspectors = [], isLoading: loadingInspectors } = useQuery({
    queryKey: ["inspectors"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles_with_email")
        .select("*")
        .eq("role", "inspector")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const { data: properties = [], isLoading: loadingProperties } = useQuery({
    queryKey: ["properties"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("properties")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const { data: jobs = [], isLoading: loadingJobs } = useQuery({
    queryKey: ["jobs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("jobs")
        .select("*")
        .order("scheduled_start", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const { data: photos = [], isLoading: loadingPhotos } = useQuery({
    queryKey: ["photos"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("photos")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      return data;
    },
  });

  const { data: syncLogs = [] } = useQuery({
    queryKey: ["syncLogs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sync_logs")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(20);
      if (error) return []; // Optional table, don't throw error
      return data;
    },
  });

  const isLoading = loadingInspectors || loadingProperties || loadingJobs || loadingPhotos;

  const connectedInspectors = inspectors.filter(i => i.dropbox_connected).length;
  const activeJobs = jobs.filter(j => j.status === "in_progress" || j.status === "scheduled").length;
  const unassignedPhotos = photos.filter(p => p.assignment_status === "unassigned").length;
  const matchedPhotos = photos.filter(p => p.assignment_status === "auto_matched" || p.assignment_status === "manual_assigned").length;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <motion.h1
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-2xl font-bold text-slate-900"
        >
          Dashboard
        </motion.h1>
        <p className="text-slate-500 mt-1">
          Real-time overview of your inspection photo pipeline
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {isLoading ? (
          Array(4).fill(0).map((_, i) => (
            <Skeleton key={i} className="h-32 rounded-2xl" />
          ))
        ) : (
          <>
            <StatsCard
              title="Total Inspectors"
              value={inspectors.length}
              subtitle={`${connectedInspectors} connected to Dropbox`}
              icon={Users}
              color="blue"
            />
            <StatsCard
              title="Properties"
              value={properties.length}
              subtitle="Active properties"
              icon={Building2}
              color="purple"
            />
            <StatsCard
              title="Active Jobs"
              value={activeJobs}
              subtitle={`${jobs.filter(j => j.status === "completed").length} completed`}
              icon={Calendar}
              color="green"
            />
            <StatsCard
              title="Photos Processed"
              value={photos.length}
              subtitle={`${matchedPhotos} matched, ${unassignedPhotos} pending`}
              icon={Image}
              color="orange"
            />
          </>
        )}
      </div>

      {/* Alerts */}
      {unassignedPhotos > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-4"
        >
          <div className="p-2 rounded-lg bg-amber-100">
            <AlertCircle className="w-5 h-5 text-amber-600" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-amber-800">
              {unassignedPhotos} photos need manual assignment
            </p>
            <p className="text-sm text-amber-600">
              These photos couldn't be automatically matched to a job
            </p>
          </div>
        </motion.div>
      )}

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RecentActivity photos={photos} syncLogs={syncLogs} />
        <InspectorStatus inspectors={inspectors} />
      </div>
    </div>
  );
}